/**
 * InsertImageButton — toolbar Insert → Image entry point.
 *
 * Opens a hidden <input type="file" accept="image/*">, reads the chosen
 * blob into a Uint8Array, probes the image's intrinsic dimensions via an
 * off-DOM Image, then dispatches Command::InsertImage with the ImageBlob
 * + an ImageFit mode.
 *
 * Before this component, the only way to land an image in the document
 * was via the drag-drop handler in `ts/src/input/dnd.ts` — which only
 * accepts `.docx` files. This unblocks the entire INSERT_IMAGE +
 * ImageFit matrix for manual QA.
 */
import { createSignal, type Component } from 'solid-js';
import {
    createEditorCommands,
    createEditorState,
    type ImageFit,
    type ImageBlob,
} from '@nge/core';
import './InsertImageButton.css';

const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

async function probeImageDimensions(
    bytes: Uint8Array,
    mime: string,
): Promise<{ width: number; height: number }> {
    /* createImageBitmap fails on some browsers for SVG / animated; fall
     * back to <img>. */
    const blob = new Blob([bytes as BlobPart], { type: mime });
    if (typeof createImageBitmap === 'function') {
        try {
            const bmp = await createImageBitmap(blob);
            const dims = { width: bmp.width, height: bmp.height };
            bmp.close?.();
            return dims;
        } catch {
            /* fall through */
        }
    }
    const url = URL.createObjectURL(blob);
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('image decode failed'));
            el.src = url;
        });
        return { width: img.naturalWidth, height: img.naturalHeight };
    } finally {
        URL.revokeObjectURL(url);
    }
}

const FIT_MODES: Array<{ value: ImageFit; label: string }> = [
    { value: 'FitWidth', label: 'Fit width' },
    { value: 'FitPage', label: 'Fit page' },
    { value: 'Original', label: 'Original size' },
];

export interface InsertImageButtonProps {
    /** Default fit mode applied at insert. Default: `FitWidth`. */
    defaultFit?: ImageFit;
    /** Override label. Default: "Image". */
    label?: string;
}

export const InsertImageButton: Component<InsertImageButtonProps> = (props) => {
    const cmd = createEditorCommands();
    const state = createEditorState();
    const inStory = () => state.editingStory() !== undefined;
    const [busy, setBusy] = createSignal(false);
    /* Issue #44 — a user-chosen fit mode at insert time (the enum always
       existed on `Command::InsertImage` but no UI ever set it). */
    const [fit, setFit] = createSignal<ImageFit>(props.defaultFit ?? 'FitWidth');
    let inputEl: HTMLInputElement | undefined;

    const onPick = async (file: File) => {
        if (inStory()) return;
        setBusy(true);
        try {
            const bytes = new Uint8Array(await file.arrayBuffer());
            const { width, height } = await probeImageDimensions(bytes, file.type);
            const blob: ImageBlob = {
                bytes,
                mime: file.type,
                width,
                height,
            };
            await cmd.insertImageAtCaret(blob, fit());
        } finally {
            setBusy(false);
            if (inputEl) inputEl.value = '';
        }
    };

    return (
        <div class="nge-insert-image__group">
            <button
                class="nge-btn nge-insert-image"
                type="button"
                aria-label="Insert image"
                title={
                    inStory()
                        ? 'Not available while editing a header or footer'
                        : 'Insert image'
                }
                disabled={busy() || inStory()}
                onClick={() => inputEl?.click()}
            >
                <span class="nge-insert-image__icon" aria-hidden="true">🖼</span>
                <span>{props.label ?? 'Image'}</span>
            </button>
            <select
                class="nge-insert-image__fit"
                aria-label="Image fit mode"
                title={
                    inStory()
                        ? 'Not available while editing a header or footer'
                        : 'Image fit mode'
                }
                disabled={inStory()}
                value={fit()}
                onChange={(e) => setFit(e.currentTarget.value as ImageFit)}
            >
                {FIT_MODES.map((m) => (
                    <option value={m.value}>{m.label}</option>
                ))}
            </select>
            <input
                ref={(el) => (inputEl = el)}
                type="file"
                accept={ACCEPT}
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.currentTarget.files?.[0];
                    if (file) void onPick(file);
                }}
            />
        </div>
    );
};
